import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface ConfirmDialogProps {
  /** Title rendered prominently. Use for the verb ("Discard changes?"). */
  title?: string;
  /** Body — explains consequence. */
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, the confirm button is rendered as destructive (red). */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Drop-in replacement for `window.confirm`. Lift the open/close state to the
 * caller (App), so the dialog is a controlled component. Esc cancels; Enter
 * confirms; clicking the backdrop cancels.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('confirm.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onConfirm();
      }
    };
    // capture: true so we run before App's window keydown handler
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onConfirm, onCancel]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26, 24, 20, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: 24,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 8,
          width: 'min(420px, 92vw)',
          padding: '20px 22px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {title && (
          <div className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)' }}>
            {title}
          </div>
        )}
        <div style={{ fontSize: 13.5, color: 'var(--ink-1)', lineHeight: 1.55 }}>{message}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            onClick={onCancel}
            className="mono"
            autoFocus
            style={{
              fontSize: 11.5,
              padding: '7px 14px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              background: 'white',
              color: 'var(--ink-1)',
              fontWeight: 600,
              letterSpacing: '.04em',
            }}
          >
            {resolvedCancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className="mono"
            style={{
              fontSize: 11.5,
              padding: '7px 14px',
              border: '1px solid',
              borderRadius: 4,
              background: destructive ? 'var(--red)' : 'var(--ink-0)',
              color: 'white',
              borderColor: destructive ? 'var(--red)' : 'var(--ink-0)',
              fontWeight: 600,
              letterSpacing: '.04em',
            }}
          >
            {resolvedConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
